import React, { useEffect, useMemo, useState } from "react";
import { LogOut, UserCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

type ProfilePayload = {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string | null;
    mobileNo: string | null;
    country: string | null;
    status: boolean;
    joinDate: string | null;
    resignDate: string | null;
    imageUrl: string | null;
    role: string | null;
    companyId: string | null;
    teamName: string | null;
  };
};

const formatDate = (value: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const FieldRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-sm font-medium text-slate-900 text-right break-words">{value || "-"}</div>
  </div>
);

export default function Profile() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState<ProfilePayload["user"] | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setError("Your login has expired. Please log out and log in again.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: any = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(json?.message || `Failed to load profile (${res.status}).`);
          return;
        }
        setProfile(json?.user ?? null);
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadProfile();
  }, [apiUrl, token]);

  const fullName =
    profile ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim() : "";

  const handleLogout = async () => {
    try {
      if (token) {
        await fetch(`${apiUrl}/supervisor/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignore logout logging failures and continue local logout.
    }
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("userTeamName");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <div className="h-[102px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-normal opacity-90 truncate">Profile</div>
              <div className="text-base font-bold mt-1 truncate">{fullName || "User"}</div>
              {profile?.teamName ? (
                <div className="text-xs font-medium opacity-90 truncate mt-0.5">{profile.teamName}</div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Logout"
            >
              <LogOut size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 bg-white">
        <div className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] p-4">
          <div className="flex items-center gap-2 text-primary mb-3">
            <UserCircle2 size={18} />
            <div className="text-sm font-semibold">User Details</div>
          </div>

          {loading ? <div className="text-sm text-slate-500 py-2">Loading profile...</div> : null}
          {error ? <div className="text-sm text-red-600 py-2">{error}</div> : null}

          {!loading && !error && profile ? (
            <div>
              <FieldRow label="First Name" value={profile.firstName || "-"} />
              <FieldRow label="Last Name" value={profile.lastName || "-"} />
              <FieldRow label="Username" value={profile.username || "-"} />
              <FieldRow label="Email" value={profile.email || "-"} />
              <FieldRow label="Mobile No" value={profile.mobileNo || "-"} />
              <FieldRow label="Country" value={profile.country || "-"} />
              <FieldRow label="Role" value={profile.role || "-"} />
              <FieldRow label="Team" value={profile.teamName || "-"} />
              <FieldRow label="Status" value={profile.status ? "Active" : "Inactive"} />
              <FieldRow label="Join Date" value={formatDate(profile.joinDate)} />
              <FieldRow label="Resign Date" value={formatDate(profile.resignDate)} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="h-6" />
    </div>
  );
}

